import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  Sparkles, Send, Target, CheckCircle, Clock, Brain, ArrowRight, RefreshCw, Lock, 
  AlertTriangle, TrendingUp, Calendar, Shield, XCircle, BarChart3, Zap, Eye,
  ChevronRight, BookOpen, PenTool, Cpu, Ghost, Ban, ExternalLink
} from "lucide-react";
import { AIOutputDisclaimer } from "@/components/LegalDisclaimers";

const ClarityAssistantPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const isPro = user?.role === "PRO";
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [flowStep, setFlowStep] = useState(0);
  const [showInput, setShowInput] = useState(false);
  
  // New feature states
  const [activeTab, setActiveTab] = useState("chat");
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [riskAlerts, setRiskAlerts] = useState([]);
  const [multiDayPlan, setMultiDayPlan] = useState(null);
  const [weeklyReflection, setWeeklyReflection] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  
  // Intelligence Engine integration states
  const [intelligenceData, setIntelligenceData] = useState(null);
  const [loadingIntelligence, setLoadingIntelligence] = useState(false);
  
  // Flow context
  const [flowContext, setFlowContext] = useState({});

  // Initialize on mount
  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
      if (mounted && messages.length === 0) {
        await initializeAssistant();
      }
    };
    
    init();
    
    return () => { mounted = false; };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch performance summary when tab changes
  useEffect(() => {
    if (activeTab === "insights" && !performanceSummary) {
      fetchPerformanceSummary();
    }
    if (activeTab === "insights" && !intelligenceData) {
      fetchIntelligenceData();
    }
  }, [activeTab]);

  const initializeAssistant = async () => {
    if (messages.length > 0) return;
    
    setLoading(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/clarity/init`);
      setMessages([{ 
        role: "assistant", 
        content: data.message, 
        buttons: data.buttons, 
        meta: data.meta,
        timestamp: new Date().toISOString() 
      }]);
      
      // If there are risk alerts, show them
      if (data.meta?.risk_alerts > 0) {
        fetchRiskAlerts();
      }
    } catch (err) {
      setMessages([{ 
        role: "assistant", 
        content: "Hi. I'm your Clarity Assistant.\nI help you decide what matters — and ignore what doesn't.", 
        buttons: [
          "🧭 What should I focus on?",
          "✍️ Improve my answer",
          "🧮 CSAT check",
          "📊 My performance",
          "📅 Plan my day",
          "🎯 Weak area analysis",
          "⚡ Quick revision strategy",
          "🔥 Prelims countdown plan"
        ],
        timestamp: new Date().toISOString() 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceSummary = async () => {
    setLoadingPerformance(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/clarity/performance-summary`);
      setPerformanceSummary(data);
      
      // Also fetch risk alerts for PRO users
      if (isPro && data.risk_alerts) {
        setRiskAlerts(data.risk_alerts);
      }
    } catch (err) {
      toast({ variant: "destructive", description: "Could not fetch performance data." });
    } finally {
      setLoadingPerformance(false);
    }
  };

  const fetchIntelligenceData = async () => {
    setLoadingIntelligence(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/intelligence/report`);
      setIntelligenceData(data);
    } catch (err) {
      console.error("Error fetching intelligence data:", err);
    } finally {
      setLoadingIntelligence(false);
    }
  };

  const fetchRiskAlerts = async () => {
    if (!isPro) return;
    try {
      const { data } = await managedAxios.get(`${API_BASE}/clarity/risk-alerts`);
      setRiskAlerts(data.alerts || []);
    } catch (err) {
      console.error("Error fetching risk alerts:", err);
    }
  };

  const fetchMultiDayPlan = async (days = 5) => {
    if (!isPro) {
      toast({ description: "🔒 Multi-day planning is a PRO feature." });
      navigate("/upgrade");
      return;
    }
    
    setLoadingPlan(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/clarity/multi-day-plan?days=${days}`);
      if (data.meta?.full_plan) {
        setMultiDayPlan(data.meta.full_plan);
      }
    } catch (err) {
      toast({ variant: "destructive", description: "Could not generate plan." });
    } finally {
      setLoadingPlan(false);
    }
  };

  const fetchWeeklyReflection = async () => {
    if (!isPro) {
      toast({ description: "🔒 Weekly reflection is a PRO feature." });
      navigate("/upgrade");
      return;
    }
    
    try {
      const { data } = await managedAxios.get(`${API_BASE}/clarity/weekly-reflection`);
      setWeeklyReflection(data);
      // Add to chat messages
      addMessage("assistant", data.message, data.buttons, data.meta);
    } catch (err) {
      toast({ variant: "destructive", description: "Could not fetch reflection." });
    }
  };

  const planTomorrow = async () => {
    if (!isPro) {
      toast({ description: "🔒 Plan tomorrow is a PRO feature." });
      navigate("/upgrade");
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/clarity/plan-tomorrow`);
      addMessage("assistant", data.message, data.buttons, data.meta);
    } catch (err) {
      toast({ variant: "destructive", description: "Could not plan tomorrow's task." });
    } finally {
      setLoading(false);
    }
  };

  const addMessage = (role, content, buttons = null, meta = null) => {
    setMessages(prev => [...prev, { 
      role, 
      content, 
      buttons, 
      meta,
      timestamp: new Date().toISOString() 
    }]);
  };

  const handleButtonClick = async (button) => {
    addMessage("user", button);
    setLoading(true);

    try {
      // Route based on button and current flow
      if (button === "Yes" || button === "🧭 What should I focus on?" || button === "🧭 What should I focus on today?") {
        const { data } = await managedAxios.post(`${API_BASE}/clarity/daily-task/start`);
        addMessage("assistant", data.message, data.buttons, data.meta);
        setCurrentFlow("daily_task");
        setFlowStep(1);
      } 
      else if (button.includes("minutes") || button.includes("hour")) {
        const { data } = await managedAxios.post(`${API_BASE}/clarity/daily-task/generate?time_available=${encodeURIComponent(button)}`);
        addMessage("assistant", data.message, data.buttons, data.meta);
        setFlowStep(2);
      }
      else if (button === "Start now" || button === "Yes, let's go") {
        const meta = messages[messages.length - 1]?.meta;
        if (meta?.stop_signal) {
          addMessage("assistant", meta.stop_signal, ["Mark as done", "I need more time"]);
        } else {
          addMessage("assistant", "After this, stop.\nDoing more today adds little value.", ["Mark as done", "I need more time"]);
        }
        setFlowStep(3);
      }
      else if (button === "Mark as done") {
        const { data } = await managedAxios.post(`${API_BASE}/clarity/daily-task/complete`);
        addMessage("assistant", data.message, data.buttons, data.meta);
        setCurrentFlow(null);
        setFlowStep(0);
      }
      else if (button === "✍️ Improve my answer") {
        addMessage("assistant", "Paste your answer below and I'll analyze it.", null);
        setCurrentFlow("improve_answer");
        setShowInput(true);
      }
      else if (button === "🧮 CSAT check") {
        addMessage("assistant", "Let's check your CSAT readiness.\n\nHow confident do you feel about CSAT?", ["Confident", "Unsure", "Afraid"]);
        setCurrentFlow("csat_check");
        setFlowStep(1);
      }
      else if (["Confident", "Unsure", "Afraid"].includes(button)) {
        setFlowContext({ ...flowContext, confidence: button.toLowerCase() });
        addMessage("assistant", "Which area concerns you most?", ["RC", "Quant", "Reasoning", "Time management"]);
        setFlowStep(2);
      }
      else if (["RC", "Quant", "Reasoning", "Time management"].includes(button)) {
        const weakArea = button.toLowerCase().replace(" management", "");
        const confidence = flowContext.confidence || "unsure";
        const { data } = await managedAxios.post(`${API_BASE}/clarity/flow/csat-check?confidence=${confidence}&weak_area=${weakArea}`);
        addMessage("assistant", data.message, data.buttons, data.meta);
        setCurrentFlow(null);
        setFlowStep(0);
      }
      else if (button === "🗓️ Plan my day" || button === "🟪 Plan my day") {
        addMessage("assistant", "How intense should today be?", ["Light (15–20 min)", "Normal (30–45 min)", "Serious (1–2 hrs)"]);
        setCurrentFlow("plan_day");
        setFlowStep(1);
      }
      else if (button.includes("Light") || button.includes("Normal") || button.includes("Serious")) {
        const intensity = button.toLowerCase().includes("light") ? "light" : 
                         button.toLowerCase().includes("normal") ? "normal" : "serious";
        const { data } = await managedAxios.post(`${API_BASE}/clarity/flow/plan-day?intensity=${intensity}`);
        addMessage("assistant", data.message, data.buttons, data.meta);
        setCurrentFlow(null);
      }
      else if (button === "📊 My performance") {
        setActiveTab("insights");
        addMessage("assistant", "Opening your performance insights...", ["Back to chat"]);
      }
      else if (button === "Back to chat") {
        setActiveTab("chat");
      }
      else if (button === "Show me other options" || button === "Show me options") {
        addMessage("assistant", "What would you like to focus on?", [
          "🧭 What should I focus on?",
          "✍️ Improve my answer",
          "🧮 CSAT check",
          "📊 My performance"
        ]);
      }
      else if (button === "Plan tomorrow" && isPro) {
        await planTomorrow();
      }
      else if (button === "Show my reflection" && isPro) {
        await fetchWeeklyReflection();
      }
      else if (button === "Plan next week" && isPro) {
        await fetchMultiDayPlan(7);
        addMessage("assistant", "Created a 7-day plan for you. Check the Insights tab.", ["View plan", "Back to chat"]);
      }
      else if (button === "View plan") {
        setActiveTab("insights");
      }
      else if (button === "Address this now") {
        // Handle risk alert action
        if (riskAlerts.length > 0) {
          const alert = riskAlerts[0];
          addMessage("assistant", `Let's address: ${alert.title}\n\n${alert.action}`, ["Start now", "Show me other options"]);
        }
      }
      else if (button === "Not now" || button === "Close" || button === "Got it") {
        addMessage("assistant", "I'm here when you need clarity. Just open me anytime.", null);
        setCurrentFlow(null);
        setFlowStep(0);
      }
      else if (button === "Start CSAT practice") {
        window.location.href = "/csat-suite";
      }
      else if (button === "Change time" || button === "Adjust intensity" || button === "Adjust task") {
        if (currentFlow === "plan_day") {
          addMessage("assistant", "How intense should today be?", ["Light (15–20 min)", "Normal (30–45 min)", "Serious (1–2 hrs)"]);
        } else {
          addMessage("assistant", "How much time do you have?", ["10–15 minutes", "30 minutes", "1 hour"]);
        }
      }
      else if (button === "3 days" || button === "5 days" || button === "7 days") {
        const days = parseInt(button);
        await fetchMultiDayPlan(days);
        addMessage("assistant", `Created a ${days}-day plan for you. Check the Insights tab.`, ["View plan", "Back to chat"]);
      }
      else if (["Anxious", "Confused", "Overwhelmed"].includes(button) || button === "I'll do it") {
        const { data } = await managedAxios.post(`${API_BASE}/clarity/emotional-support?emotion=${button.toLowerCase()}`);
        addMessage("assistant", data.message, data.buttons, data.meta);
        setCurrentFlow(null);
      }
      else if (button === "I need more help") {
        addMessage("assistant", "What's on your mind?\n\nI'm here to help you find clarity.", ["Anxious", "Confused", "Overwhelmed"]);
      }
      // New strategic coaching buttons
      else if (button === "🎯 Weak area analysis") {
        const { data } = await managedAxios.post(`${API_BASE}/clarity/flow/weak-area-analysis`);
        addMessage("assistant", data.message, data.buttons, data.meta);
      }
      else if (button === "⚡ Quick revision strategy") {
        addMessage("assistant", "How much time do you have for revision?", [
          "30 minutes",
          "1 hour", 
          "2+ hours",
          "Full day"
        ]);
        setCurrentFlow("revision_strategy");
      }
      else if (currentFlow === "revision_strategy" && ["30 minutes", "1 hour", "2+ hours", "Full day"].includes(button)) {
        const timeMap = { "30 minutes": "30min", "1 hour": "1hr", "2+ hours": "2hr", "Full day": "fullday" };
        const { data } = await managedAxios.post(`${API_BASE}/clarity/flow/revision-strategy?time=${timeMap[button]}`);
        addMessage("assistant", data.message, data.buttons, data.meta);
        setCurrentFlow(null);
      }
      else if (button === "🔥 Prelims countdown plan") {
        const { data } = await managedAxios.post(`${API_BASE}/clarity/flow/prelims-countdown`);
        addMessage("assistant", data.message, data.buttons, data.meta);
      }
      else {
        const { data } = await managedAxios.post(`${API_BASE}/clarity/chat`, { message: button });
        addMessage("assistant", data.message, data.buttons, data.meta);
      }
    } catch (err) {
      console.error("Error:", err);
      const detail = err.response?.data?.detail;
      if (detail?.includes("PRO")) {
        addMessage("assistant", "🔒 This feature requires PRO access.\n\nUpgrade to PRO for personalised, history-aware guidance.", ["Got it"]);
      } else {
        toast({ variant: "destructive", description: detail || "Something went wrong." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;
    
    const text = inputText.trim();
    setInputText("");
    addMessage("user", text);
    setLoading(true);

    try {
      if (currentFlow === "improve_answer") {
        const { data } = await managedAxios.post(`${API_BASE}/clarity/flow/improve-answer`, { 
          answer_text: text 
        });
        addMessage("assistant", data.message, data.buttons, data.meta);
        setShowInput(false);
        setCurrentFlow(null);
      } else {
        const { data } = await managedAxios.post(`${API_BASE}/clarity/chat`, { message: text });
        addMessage("assistant", data.message, data.buttons, data.meta);
        if (data.meta?.flow) {
          setCurrentFlow(data.meta.flow);
        }
      }
    } catch (err) {
      console.error("Error:", err);
      toast({ variant: "destructive", description: "Could not process your message." });
    } finally {
      setLoading(false);
    }
  };

  const renderMessage = (msg, idx) => {
    const isUser = msg.role === "user";
    
    return (
      <div key={idx} className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
        <div className={`max-w-[85%] ${isUser ? "order-2" : "order-1"}`}>
          {!isUser && (
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-xs font-medium text-slate-500">Clarity Assistant</span>
            </div>
          )}
          
          <Card className={`${isUser 
            ? "bg-indigo-600 text-white border-indigo-600" 
            : "bg-white border-slate-200"}`}
          >
            <CardContent className="p-4">
              <div className={`text-sm whitespace-pre-wrap ${isUser ? "text-white" : "text-slate-800"}`}>
                {msg.content.split('\n').map((line, i) => {
                  if (line.startsWith('**') && line.endsWith('**')) {
                    return <p key={i} className="font-bold mb-2">{line.replace(/\*\*/g, '')}</p>;
                  }
                  if (line.startsWith('•') || line.startsWith('-') || line.startsWith('✔') || line.startsWith('❌') || line.startsWith('⚠️')) {
                    return <p key={i} className="ml-2 mb-1">{line}</p>;
                  }
                  return <p key={i} className={line ? "mb-2" : "mb-1"}>{line}</p>;
                })}
              </div>

              {msg.meta && !isUser && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  {msg.meta.task_completed && (
                    <div className="flex items-center gap-2 text-green-600 text-xs">
                      <CheckCircle className="w-4 h-4" />
                      <span>Task completed</span>
                    </div>
                  )}
                  {msg.meta.time_saved_total && (
                    <div className="flex items-center gap-2 text-indigo-600 text-xs mt-1">
                      <Clock className="w-4 h-4" />
                      <span>~{msg.meta.time_saved_total} min saved</span>
                    </div>
                  )}
                  {msg.meta.consecutive_days > 0 && (
                    <div className="flex items-center gap-2 text-amber-600 text-xs mt-1">
                      <Zap className="w-4 h-4" />
                      <span>{msg.meta.consecutive_days} day streak!</span>
                    </div>
                  )}
                  {msg.meta.risk && (
                    <Badge className={`mt-2 ${
                      msg.meta.risk === "safe" ? "bg-green-100 text-green-800" :
                      msg.meta.risk === "borderline" ? "bg-yellow-100 text-yellow-800" :
                      "bg-red-100 text-red-800"
                    }`}>
                      Risk: {msg.meta.risk.charAt(0).toUpperCase() + msg.meta.risk.slice(1)}
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {msg.buttons && !isUser && (
            <div className="flex flex-wrap gap-2 mt-3">
              {msg.buttons.map((btn, btnIdx) => (
                <Button
                  key={btnIdx}
                  variant="outline"
                  size="sm"
                  onClick={() => handleButtonClick(btn)}
                  disabled={loading}
                  className="text-sm hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-all"
                >
                  {btn}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Performance Insights Tab
  const renderInsightsTab = () => {
    if (loadingPerformance) {
      return (
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
          <span className="ml-2 text-slate-600">Loading your insights...</span>
        </div>
      );
    }

    if (!performanceSummary) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <BarChart3 className="w-12 h-12 text-slate-300 mb-4" />
          <p className="text-slate-600">No performance data yet.</p>
          <Button onClick={fetchPerformanceSummary} className="mt-4">
            Refresh Data
          </Button>
        </div>
      );
    }

    const { prelims, mains, csat, overall, risk_alerts, mark_loss_diagnosis, answer_patterns } = performanceSummary;

    return (
      <div className="p-4 space-y-4 overflow-y-auto h-[60vh]">
        {/* Link to Full Intelligence Engine */}
        <Card className="border-slate-900 bg-gradient-to-r from-slate-900 to-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                  <Cpu className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-white">Core Intelligence Engine</p>
                  <p className="text-xs text-slate-400">Selection probability • Exam simulation • Topper benchmark</p>
                </div>
              </div>
              <Button 
                size="sm" 
                className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white"
                onClick={() => window.location.href = "/intelligence"}
              >
                View Full Report <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Selection Probability Summary (from Intelligence Engine) */}
        {intelligenceData && (
          <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-indigo-600" />
                Selection Probability
                <Badge variant="outline" className="ml-auto text-xs">
                  From Intelligence Engine
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className={`text-4xl font-bold ${
                  intelligenceData.selection_probability.probability >= 60 ? "text-green-600" :
                  intelligenceData.selection_probability.probability >= 40 ? "text-amber-600" :
                  "text-red-600"
                }`}>
                  {intelligenceData.selection_probability.probability}%
                </div>
                <div className="flex-1">
                  <Badge className={`${
                    intelligenceData.selection_probability.trend === "improving" ? "bg-green-100 text-green-800" :
                    intelligenceData.selection_probability.trend === "declining" ? "bg-red-100 text-red-800" :
                    "bg-slate-100 text-slate-800"
                  }`}>
                    {intelligenceData.selection_probability.trend?.charAt(0).toUpperCase() + intelligenceData.selection_probability.trend?.slice(1)}
                  </Badge>
                  <p className="text-xs text-slate-600 mt-2">{intelligenceData.selection_probability.hard_truth}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Topper Benchmark Summary (from Intelligence Engine) */}
        {intelligenceData && (
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Ghost className="w-4 h-4 text-purple-600" />
                Topper Benchmark
                <Badge variant="outline" className="ml-auto text-xs bg-purple-100 text-purple-700 border-0">
                  vs Top 10%
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Overall Gap</span>
                  <span className={`font-bold ${
                    intelligenceData.topper_benchmark.overall_gap_percentage <= 10 ? "text-green-600" :
                    intelligenceData.topper_benchmark.overall_gap_percentage <= 25 ? "text-amber-600" :
                    "text-red-600"
                  }`}>
                    {intelligenceData.topper_benchmark.overall_gap_percentage}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Closest to toppers:</span>
                  <span className="text-green-600 font-medium">{intelligenceData.topper_benchmark.closest_metric}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Needs most work:</span>
                  <span className="text-red-600 font-medium">{intelligenceData.topper_benchmark.furthest_metric}</span>
                </div>
                <p className="text-xs text-slate-600 mt-2 pt-2 border-t">{intelligenceData.topper_benchmark.message}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* What to Skip Today Section */}
        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-red-700">
              <Ban className="w-4 h-4" />
              What to Skip Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            {performanceSummary?.prelims?.stop_studying_list?.length > 0 ? (
              <div className="space-y-2">
                {performanceSummary.prelims.stop_studying_list.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
                <p className="text-xs text-slate-500 mt-2 pt-2 border-t">
                  These topics have diminishing returns right now. Focus on high-impact areas instead.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Avoid random reading without purpose</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Skip new topics if you have weak fundamentals</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Do not chase viral current affairs</span>
                </div>
                <p className="text-xs text-slate-500 mt-2 pt-2 border-t">
                  Take more tests to get personalized skip recommendations.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Risk Alerts (PRO) */}
        {isPro && riskAlerts.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-4 h-4" />
                Risk Alerts ({riskAlerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {riskAlerts.map((alert, idx) => (
                <div key={idx} className="p-3 bg-white rounded-lg border border-red-100">
                  <p className="font-medium text-slate-800 text-sm">{alert.title}</p>
                  <p className="text-xs text-slate-600 mt-1">{alert.message}</p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-2 text-xs"
                    onClick={() => {
                      setActiveTab("chat");
                      handleButtonClick("Address this now");
                    }}
                  >
                    Address Now
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Prelims Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="w-4 h-4 text-indigo-600" />
              Prelims Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {prelims?.has_data ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Safety Status</span>
                  <Badge className={`${
                    prelims.safety_status === "safe" ? "bg-green-100 text-green-800" :
                    prelims.safety_status === "borderline" ? "bg-amber-100 text-amber-800" :
                    "bg-red-100 text-red-800"
                  }`}>
                    {prelims.safety_status?.toUpperCase()}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Tests Taken</span>
                  <span className="font-medium">{prelims.total_tests}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Average Score</span>
                  <span className="font-medium">{prelims.average_score}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Trend</span>
                  <Badge variant="outline" className={`${
                    prelims.score_trend === "improving" ? "text-green-600" :
                    prelims.score_trend === "declining" ? "text-red-600" : "text-slate-600"
                  }`}>
                    {prelims.score_trend?.charAt(0).toUpperCase() + prelims.score_trend?.slice(1)}
                  </Badge>
                </div>
                {prelims.weak_areas?.length > 0 && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-slate-500 mb-2">Weak Areas:</p>
                    <div className="flex flex-wrap gap-1">
                      {prelims.weak_areas.slice(0, 3).map((area, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs text-red-600 border-red-200">
                          {area.topic} ({area.accuracy}%)
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">{prelims?.message || "No data yet"}</p>
            )}
          </CardContent>
        </Card>

        {/* CSAT Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="w-4 h-4 text-purple-600" />
              CSAT Safety
            </CardTitle>
          </CardHeader>
          <CardContent>
            {csat?.has_data ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Risk Level</span>
                  <Badge className={`${
                    csat.risk === "safe" ? "bg-green-100 text-green-800" :
                    csat.risk === "borderline" ? "bg-amber-100 text-amber-800" :
                    "bg-red-100 text-red-800"
                  }`}>
                    {csat.risk?.toUpperCase()}
                  </Badge>
                </div>
                {csat.weak_area && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Weak Area</span>
                    <span className="font-medium text-red-600">{csat.weak_area.toUpperCase()}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Practice Sessions</span>
                  <span className="font-medium">{csat.total_practice_sessions}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">{csat?.message || "No CSAT data yet"}</p>
            )}
          </CardContent>
        </Card>

        {/* Mains Status (PRO) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <PenTool className="w-4 h-4 text-emerald-600" />
              Mains Progress
              {!isPro && <Lock className="w-3 h-3 text-slate-400" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mains?.has_data ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Answers Generated</span>
                  <span className="font-medium">{mains.total_answers_generated}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Evaluations</span>
                  <span className="font-medium">{mains.total_evaluations}</span>
                </div>
                {mains.neglected_suites?.length > 0 && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-slate-500 mb-2">Neglected Suites:</p>
                    <div className="flex flex-wrap gap-1">
                      {mains.neglected_suites.map((suite, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs text-amber-600 border-amber-200">
                          {suite}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">{mains?.message || "🔒 PRO feature"}</p>
            )}
          </CardContent>
        </Card>

        {/* Mark Loss Diagnosis (PRO) */}
        {isPro && mark_loss_diagnosis?.has_data && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                <XCircle className="w-4 h-4" />
                Mark Loss Diagnosis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700">{mark_loss_diagnosis.diagnosis}</p>
              <p className="text-xs text-slate-500 mt-2">
                <strong>Fix:</strong> {mark_loss_diagnosis.fix}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Overall Stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-600" />
              Your Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-indigo-600">{overall?.tasks_completed || 0}</p>
                <p className="text-xs text-slate-500">Tasks Done</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{overall?.consecutive_days || 0}</p>
                <p className="text-xs text-slate-500">Day Streak</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">~{overall?.time_saved || 0}</p>
                <p className="text-xs text-slate-500">Min Saved</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Multi-Day Plan (PRO) */}
        {isPro && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  Multi-Day Plan
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => fetchMultiDayPlan(5)}
                  disabled={loadingPlan}
                  className="text-xs"
                >
                  {loadingPlan ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Generate 5-Day Plan"}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {multiDayPlan ? (
                <div className="space-y-3">
                  {multiDayPlan.slice(0, 5).map((day, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium text-sm text-indigo-700">{day.date_label}</p>
                      <p className="text-sm text-slate-700 mt-1">{day.primary_task?.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{day.primary_task?.duration}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Click to generate a personalized multi-day plan.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* PRO Upgrade for FREE users */}
        {!isPro && (
          <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="font-medium text-slate-800">Unlock Full Insights</p>
                  <p className="text-xs text-slate-600">PRO gets risk alerts, mark-loss diagnosis, multi-day plans</p>
                </div>
              </div>
              <Button 
                className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700"
                onClick={() => window.location.href = "/upgrade"}
              >
                Upgrade to PRO <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/30">
      {/* Premium Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-amber-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-yellow-100/40 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <div className="relative glass-card border-0 rounded-none">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shadow-lg">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Clarity Assistant</h1>
                <p className="text-sm text-slate-600">Your daily decision guide</p>
              </div>
            </div>
            <Badge className={`px-3 py-1 ${isPro 
              ? "bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-0" 
              : "bg-slate-100 text-slate-600 border-0"}`}>
              {isPro ? "PRO" : "FREE"}
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <div className="relative max-w-4xl mx-auto px-6 py-6">
        <div className="glass-card rounded-2xl shadow-premium overflow-hidden animate-fade-up">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none bg-white/50 p-1">
              <TabsTrigger value="chat" className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Chat
              </TabsTrigger>
              <TabsTrigger value="insights" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Insights
                {isPro && riskAlerts.length > 0 && (
                  <Badge className="bg-red-500 text-white text-[10px] px-1.5">{riskAlerts.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Chat Tab */}
            <TabsContent value="chat" className="mt-0">
              <div className="h-[60vh] overflow-y-auto p-4 bg-white/50">
                {messages.map((msg, idx) => renderMessage(msg, idx))}
                
                {loading && (
                  <div className="flex justify-start mb-4">
                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 animate-spin text-indigo-600" />
                        <span className="text-sm text-slate-600">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {showInput && (
                <div className="border-t border-slate-200 p-4 bg-white">
                  <div className="flex gap-3">
                    <Textarea
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder={currentFlow === "improve_answer" 
                        ? "Paste your answer here..." 
                        : "Type your message..."}
                      className="flex-1 min-h-[80px] resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={loading || !inputText.trim()}
                      className="bg-indigo-600 hover:bg-indigo-700 self-end"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-2">
                    Press Enter to send, Shift+Enter for new line
                    <AIOutputDisclaimer variant="tooltip" />
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights" className="mt-0">
              {renderInsightsTab()}
            </TabsContent>
          </Tabs>
        </div>

        {/* Quick Actions */}
        {!showInput && activeTab === "chat" && messages.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setMessages([]);
                initializeAssistant();
              }}
              className="text-slate-600 hover:text-indigo-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Start fresh
            </Button>
            {isPro && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={planTomorrow}
                  className="text-slate-600 hover:text-indigo-600"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Plan tomorrow
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchWeeklyReflection}
                  className="text-slate-600 hover:text-indigo-600"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Weekly reflection
                </Button>
              </>
            )}
          </div>
        )}

        {/* PRO Upgrade Banner */}
        {!isPro && (
          <Card className="mt-6 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-indigo-600" />
                  <div>
                    <p className="font-medium text-slate-800">Unlock Full Power</p>
                    <p className="text-sm text-slate-600">PRO gets risk alerts, multi-day plans & answer tracking</p>
                  </div>
                </div>
                <Button 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => window.location.href = "/upgrade"}
                >
                  Upgrade <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClarityAssistantPage;
